import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '../../lib/cn'
import { useT } from '../../lib/i18n'
import { Icon } from './Icon'
import type { IconName } from './Icon'
import { IconButton } from './IconButton'

export type ToastTone = 'success' | 'error' | 'warn' | 'info'

export interface ToastOptions {
  /**
   * Repeats the verb of the control that caused it: `Post scheduled`. Already translated —
   * a toast is raised from a call site that knows which verb it used.
   */
  title: string
  description?: ReactNode
  tone?: ToastTone
  /** Errors stay until dismissed; everything else clears itself. */
  durationMs?: number
  action?: { label: string; onClick: () => void }
}

interface ToastRecord extends ToastOptions {
  id: string
  tone: ToastTone
}

interface ToastContextValue {
  toast: (options: ToastOptions) => string
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const TONE_ICON: Record<ToastTone, IconName> = {
  success: 'check',
  error: 'alert',
  warn: 'alert',
  info: 'info',
}

const TONE_COLOR: Record<ToastTone, string> = {
  success: 'text-up-deep',
  error: 'text-down-deep',
  warn: 'text-warn-deep',
  info: 'text-info',
}

let counter = 0

export const ToastProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const t = useT()
  const [toasts, setToasts] = useState<ToastRecord[]>([])
  const timers = useRef(new Map<string, number>())

  const dismiss = useCallback((id: string) => {
    setToasts((previous) => previous.filter((entry) => entry.id !== id))
    const handle = timers.current.get(id)
    if (handle !== undefined) {
      window.clearTimeout(handle)
      timers.current.delete(id)
    }
  }, [])

  const toast = useCallback(
    (options: ToastOptions): string => {
      counter += 1
      const id = `toast-${counter}`
      const tone = options.tone ?? 'success'
      const record: ToastRecord = { ...options, id, tone }
      setToasts((previous) => [...previous, record].slice(-4))

      // A toast carrying its own action (e.g. Undo) is still waiting on that click — timing it
      // out at 5s regardless of tone takes the action away before most people can reach it.
      const duration = options.durationMs ?? (tone === 'error' || options.action ? 0 : 5000)
      if (duration > 0) {
        const handle = window.setTimeout(() => {
          dismiss(id)
        }, duration)
        timers.current.set(id, handle)
      }
      return id
    },
    [dismiss],
  )

  useEffect(() => {
    const handles = timers.current
    return () => {
      for (const handle of handles.values()) window.clearTimeout(handle)
      handles.clear()
    }
  }, [])

  const value = useMemo<ToastContextValue>(() => ({ toast, dismiss }), [toast, dismiss])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div
          aria-live="polite"
          aria-atomic="false"
          className="pointer-events-none fixed inset-x-0 bottom-0 z-80 flex flex-col items-center gap-2 p-4 sm:inset-x-auto sm:right-0 sm:items-end"
        >
          {toasts.map((entry) => (
            <div
              key={entry.id}
              role={entry.tone === 'error' ? 'alert' : 'status'}
              className={cn(
                'anim-rise pointer-events-auto flex w-full max-w-sm items-start gap-3',
                'rounded-md border border-hair bg-surface px-4 py-3 shadow-float',
              )}
            >
              <Icon
                name={TONE_ICON[entry.tone]}
                size={18}
                className={cn('mt-0.5', TONE_COLOR[entry.tone])}
              />
              <div className="min-w-0 flex-1">
                <p className="t-h3 text-ink">{entry.title}</p>
                {entry.description ? (
                  <p className="t-small mt-0.5 text-ink-mute">{entry.description}</p>
                ) : null}
                {entry.action ? (
                  <button
                    type="button"
                    onClick={() => {
                      entry.action?.onClick()
                      dismiss(entry.id)
                    }}
                    className="t-small ring-focus mt-1.5 font-semibold text-brass-deep underline underline-offset-2"
                  >
                    {entry.action.label}
                  </button>
                ) : null}
              </div>
              <IconButton
                label={t('common.actions.dismiss')}
                icon={<Icon name="close" size={14} />}
                size="sm"
                tooltip={false}
                onClick={() => {
                  dismiss(entry.id)
                }}
              />
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}

/**
 * Confirmation and failure notices. The title keeps the verb of the control that triggered it,
 * so `Schedule post` is confirmed by `Post scheduled` — never by `Success`.
 */
export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>.')
  return ctx
}
